import {Component, OnInit}          from '@angular/core';
import {ActivatedRoute,
	NavigationEnd, Router}          from "@angular/router";
import {HttpErrorResponse}          from "@angular/common/http";

import {AngularQuery}               from "../../services/query/angularquery";
import {Message}                    from "../../services/message/message";
import {MessageEntry}               from "../../services/message/message-entry";
import {MessageService}             from "../../services/message/message.service";
import {News}                       from "../../services/news/news";
import {Ng4LoadingSpinnerService}   from "ng4-loading-spinner";
import {Permissions}                from "../../services/permissions/permissions";
import {Player}                     from "../../services/player/player";
import {PlayerService}              from "../../services/player/player.service";
import {Source}                     from "../../services/source/source";
import {SourceService}              from "../../services/source/source.service";
import {Sport}                      from "../../services/sport/sport";
import {Tag}                        from "../../services/tag/tag";
import {TagService}                 from "../../services/tag/tag.service";
import {Team}                       from "../../services/team/team";
import {TeamService}                from "../../services/team/team.service";
import {User}                       from "../../services/user/user";
import {Version}                    from "../../services/version/version";
import {VersionJson}                from "../../services/version/version-json";
import {VersionService}             from "../../services/version/version.service";

import {forkJoin}                   from "rxjs/observable/forkJoin";
import {Subscription}               from "rxjs/Subscription";
import {isNullOrUndefined}          from "util";


@Component({
  selector: 'app-version',
  templateUrl: './version.component.html',
  styleUrls: ['./version.component.css']
})


/**
 * This component displays the versions for
 * a given item in the 'news' table and shows
 * the content for viewing only.
 */
export class VersionComponent implements OnInit {

	isAdministrator:boolean = false;        // Whether or not the current user is an administrator.
	message: Message;                       // The Message object.
	navigationSubscription: Subscription;   // Allows reloading of same page.
	parameterSubscription: Subscription;    // Used to get the ID value of the article from the router.
	showVersionID:number = -1;              // The Version ID of the story currently displaying.
	showVersionJson:VersionJson;            // The Player, Team and Tag arrays of the current story.
	showVersionNews:News;                   // The News object of the current story.
	playerIDs:number[] = [];                // Player IDs parsed from json.
	players:Player[] = [];                  // Player information retrieved from the service.
	sourceIDs:number[] = [];                // Source IDs parsed from json.
	sources:Source[] = [];                  // Source information retrieved from the service.
	sports:Sport[];                         // The array of Sport data.
	subscription: Subscription;             // Message service subscription.
	tagIDs:number[] = [];                   // Tag IDs parsed from json.
	tags:Tag[] = [];                        // Tag information retrieved from the service.
	teamIDs:number[] = [];                  // Team IDs parsed from json.
	teams:Team[] = [];                      // Team information retrieved from the service.
	users:User[];                           // The array of User data.
	versions:Version[];                     // The array of Version data.
	versionSubscription: Subscription;      // Used when retrieving Version array.

	/**
	 * Constructor to initialize the page.
	 *
	 * @param {MessageService} messageService  Handles the MessageService singleton.
	 * @param {PlayerService} playerService    Retrieves Player objects.
	 * @param {ActivatedRoute} route           Used when retrieving the info from SportsResolve and UserResolve.
	 * @param {Router} router                  The Router singleton used for navigation.
	 * @param {SourceService} sourceService    Retrieves Source objects.
	 * @param {Ng4LoadingSpinnerService} spinnerService  The singleton for displaying the spinner on this page.
	 * @param {TagService} tagService          Retrieves Tag objects.
	 * @param {TeamService} teamService        Retrieves Team objects.
	 * @param {VersionService} versionService  Used to get the version data that will be displayed.
	 */
	constructor(

		private messageService: MessageService,
		private playerService: PlayerService,
		private route: ActivatedRoute,
		private router: Router,
		private sourceService: SourceService,
		private spinnerService: Ng4LoadingSpinnerService,
		private tagService: TagService,
		private teamService: TeamService,
		private versionService: VersionService

	) {

		// Set the defaults for the News object.
		let defaults: Object = { 'hot': 0, 'title': '', 'body': '', 'fantasy_tip': '', 'huddle_tip': '', 'baseball_tip': '',
			'code': '', 'visible': 0, 'date': new Date(), 'id': -1, 'source_id': -1, 'original_url': '',
		};
		this.showVersionNews = new News(defaults);

		// Support reload of page; this fires every time the Router completes its process.
		this.navigationSubscription = this.router.events.subscribe((e: any) => {

			// Retrieve and display the appropriate information.
			if (e instanceof NavigationEnd) {
				this.initialize();
			}

		});

	}

	/**
	 * Set up messaging.
	 */
	ngOnInit() {

		this.subscription = this.messageService.subject.subscribe( message => { this.message = message; } );

		// Mark the current user as an administrator or non-administrator.
		this.isAdministrator = Permissions.checkPermission(Permissions.SUPER_USER);

	}

	/**
	 * Unsubscribe to messages when quitting to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.navigationSubscription) {
			this.navigationSubscription.unsubscribe();
		}

		if (this.parameterSubscription) {
			this.parameterSubscription.unsubscribe();
		}

		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		if (this.versionSubscription) {
			this.versionSubscription.unsubscribe();
		}

	}


	/**
	 * Return a formatted list of Players.
	 *
	 * @param {VersionJson} json  The JSON that includes the 'players' array of Player IDs for this version.
	 * @return {string} Formatted player data or a zero-length string.
	 */
	getPlayers(json:VersionJson):string {
		// Handle initialization case.
		if (isNullOrUndefined(json) || isNullOrUndefined(json.players) || 0 === json.players.length) {
			return '';
		}
		// Find the tags that are used on this version only.
		let found:Player[] = this.players.filter((p:Player) => {
			for (let b of json.players) {
				if (b == p.id) {
					return true;
				}
			}
			return false;
		});

		// Format it for output.
		let rvalue:string = '';
		for (let f of found) {
			rvalue += f.firstname;
			rvalue += ' ' + f.surname;
			rvalue += ', ' + f.position_title;
			rvalue += ' (' + f.team_title + ')<br>';
		}
		return rvalue;
	}


	/**
	 * Return the name of the Source that
	 * matches the provided ID number.
	 *
	 * @param {number} source_id  The ID number.
	 * @return {string}  The source name or a zero-length string.
	 */
	getSource(source_id:number):string {

		// Handle initialization case.
		if (-1 === source_id) {
			return '';
		}
		let s:Source = this.sources.find( x => x.id == source_id);
		return (isNullOrUndefined(s) ? '' : s.name);

	}


	/**
	 * Return a formatted string with Tag values that can
	 * be displayed on the page.
	 *
	 * @param {VersionJson} json  The JSON that includes the 'tags' array of Tag IDs for this version.
	 * @return {string} Comma-separated tag list or zero-length string.
	 */
	getTags(json:VersionJson):string {
		// Handle initialization case.
		if (isNullOrUndefined(json) || isNullOrUndefined(json.tags) || 0 === json.tags.length || isNullOrUndefined(this.tagIDs)) {
			return '';
		}

		// Find the tags that are used on this version only.
		let found:Tag[] = this.tags.filter((t:Tag) => {
			for (let b of json.tags) {
				if (b == t.id) {
					return true;
				}
			}
			return false;
		});

		// Format it for output.
		let rvalue:string = '';
		for (let f of found) {
			if (0 !== rvalue.length){
				rvalue += ', ';
			}
			rvalue += f.tag;
		}
		return rvalue;
	}


	/**
	 * Retrieve and return the appropriate team
	 * associated with the current story version.
	 *
	 * @return {string}  The team name or a zero-length string.
	 */
	getTeam(json:VersionJson):string {

		// Handle initialization case.
		if (isNullOrUndefined(json) || isNullOrUndefined(json.team) || 0 === json.team.length) {
			return '';
		}
		let t:Team = this.teams.find( x => x.id == json.team[0]); // Team is an array, but always 1 entry.
		return (isNullOrUndefined(t) ? '' : t.title);

	}


	/**
	 * Provide the appropriate publishing status
	 * for the current story version.
	 *
	 * @param {number} status  The 'visible' value of the selected version.
	 * @return {string} The status.
	 */
	getVisible(status:number):string {

		let statuses: string[] = ['Trash', 'Draft', 'Published', 'Future'];
		status++;  // This is done to display 'Trash', which is -1.
		return statuses[status];

	}


	/**
	 * Call in the Version data for the article.
	 */
	initialize():void {

		// Turn on the spinner so they know to wait.
		// this.spinnerService.show();

		// Initialize the sports array and fill it with the retrieved data.
		this.sports = this.route.snapshot.data['sports'];
		this.users = this.route.snapshot.data['users'];

		// Get the article ID to load by subscribing to the route parameters.
		let id:number = -1;
		this.parameterSubscription = this.route.params.subscribe( params => {
			id = params['id'];
		});

		// Send the 'loading' message.
		this.messageService.sendMessageEntry(new MessageEntry({

			message: 'Loading versions for article #' + String(id) + ' ...',
			level: 1

		}));

		// Get the version information.
		this.versionSubscription = this.versionService.getVersions(id).subscribe(

			(response:Version[]) => {

				this.versions = response;
				let version_label:string = (1 === this.versions.length) ? ' version' : ' versions';
				let msg:string = 'Loaded ' + String(this.versions.length) + version_label + ' of article #' + String(id);

				if (0 === this.versions.length) {
					msg = 'No versions found for article #' + String(id);
				} else {

					let json:VersionJson = null;

					// Populate the news item from the JSON and get a list of all the tags, teams, sources and players.
					for (let v of this.versions) {
						v.news = new News(JSON.parse(v.news_json));
						json = JSON.parse(v.news_json);
						this.playerIDs = this.playerIDs.concat(json.players);
						this.sourceIDs = this.sourceIDs.concat(v.news.source_id);
						this.tagIDs = this.tagIDs.concat(json.tags);
						this.teamIDs = this.teamIDs.concat(json.team);
					}

					// Remove duplicates.
					this.playerIDs = this.playerIDs.filter((v, i, a) => a.indexOf(v) === i);
					this.sourceIDs = this.sourceIDs.filter((v, i, a) => a.indexOf(v) === i);
					this.tagIDs = this.tagIDs.filter((v, i, a) => a.indexOf(v) === i);
					this.teamIDs = this.teamIDs.filter((v, i, a) => a.indexOf(v) === i);

					// Retrieve all the data needed for this page.
					let operations:any[] = [];
					let return_type:string[] = [];

					// Get the Player data.
					if (0 !== this.playerIDs.length) {

						for (let p_id in this.playerIDs) {
							operations.push(this.playerService.getPlayerById(this.playerIDs[p_id]));
							return_type.push('Player');
						}

					}

					// Get the Source data.
					if (0 !== this.sourceIDs.length) {

						for (let s_id in this.sourceIDs) {
							operations.push(this.sourceService.getSource(this.sourceIDs[s_id]));
							return_type.push('Source');
						}

					}

					// Get the Tag data.
					if (0 !== this.tagIDs.length) {

						for (let y in this.tagIDs) {
							let query:AngularQuery = new AngularQuery();
							query.setEndpoint(AngularQuery.ENDPOINT_TAG, String(this.tagIDs[y]));
							query.useCacheBuster(false);
							query.useParams(false);
							operations.push(this.tagService.getTags(query));
							return_type.push('Tag');
						}

					}

					// Get the Team data.
					if (0 !== this.teamIDs.length) {

						for (let x in this.teamIDs) {
							operations.push(this.teamService.getTeamById(this.teamIDs[x]));
							return_type.push('Team');
						}

					}

					// Run all the queries at once and return data for them.
					forkJoin(operations).subscribe(
						(data) => {

							// The data are returned as they were pushed into the operations array.
							for (let z in data) {

								// The return_type entry tells us what kind of data it is.
								switch(return_type[z]) {

									case 'Player': {
										this.players.push(new Player(data[z] as Player));
										break;
									}

									case 'Source': {
										this.sources.push(new Source(data[z] as Source));
										break;
									}

									case 'Tag': {
										this.tags.push(new Tag(data[z] as Tag));
										break;
									}

									case 'Team': {
										this.teams.push(new Team(data[z] as Team));
										break;
									}

								}

							}

							// Send the 'Finished' message.
							this.messageService.sendMessageEntry(new MessageEntry({

								message: msg,
								level: 1

							}));

						},
						() => {

							// Send the error message.
							this.messageService.sendMessageEntry(new MessageEntry({

								message: 'Unable to retrieve data needed to display the versions',
								level: 4

							}));

						}
					);


				}

			},

			(error) => {

				this.versions = [];

				if (error instanceof HttpErrorResponse && 404 !== error.status) {

					// Send the error message.
					this.messageService.sendMessageEntry(new MessageEntry({

						message: 'Unable to retrieve article versions',
						level: 4

					}));

				}

			},

			() => {

				// Turn off the spinner so they can interact with the screen again.
				this.spinnerService.hide();

			}
		);

	}


	/**
	 * This is used to highlight the selected version.
	 *
	 * @param version  The ID of the currently selected version.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(version: number): any {

		if (version === this.showVersionID) {

			return {'color': 'blue'};

		}

		return {};

	}


	/**
	 * For a given user ID value, provide
	 * the user's name.
	 *
	 * @param {number} id  The User ID to look up.
	 * @return {string} The user name.
	 */
	userLookup(id:number):string {

		if (! this.isAdministrator) {
			return '';
		}

		let user:User = this.users.find( x => x.id === id);

		if (isNullOrUndefined( user )) {
			return 'unknown';
		}

		return user.uname;

	}


	/**
	 * Switch back to the story list.
	 *
	 */
	viewStoryList(): void {

		this.router.navigate(['/articles/list']);

	}


	/**
	 * Set the current display to the selected version number.
	 *
	 * @param {number} version
	 */
	viewVersion(version:number):void {

		this.showVersionID = version;
		this.showVersionNews = this.versions[version].news;
		let json:VersionJson = JSON.parse(this.versions[version].news_json);
		this.showVersionJson = json;

	}


}



// WEBPACK FOOTER //
// ./src/app/articles/version/version.component.ts